/**
 * User preference: how many minutes before JWT expiry the session banner
 * switches to "alert" styling (amber), then final minute as critical (red).
 * Stored per browser (localStorage), default 1 minute. Max 24 hours (1440 min).
 */
export const SESSION_ALERT_STORAGE_KEY = 'sessionAlertMinutesBeforeExpiry';
const STORAGE_KEY = SESSION_ALERT_STORAGE_KEY;
export const SESSION_ALERT_DEFAULT_MINUTES = 1;
export const SESSION_ALERT_MIN_MINUTES = 1;
/** Upper cap so the alert window cannot exceed one day (matches common JWT lifetimes). */
export const SESSION_ALERT_MAX_MINUTES = 24 * 60;
const MIN_MINUTES = SESSION_ALERT_MIN_MINUTES;
const MAX_MINUTES = SESSION_ALERT_MAX_MINUTES;

export const SESSION_ALERT_CHANGED_EVENT = 'sessionAlertMinutesChanged';

function clampMinutes(n) {
  const x = Math.round(Number(n));
  if (!Number.isFinite(x)) return SESSION_ALERT_DEFAULT_MINUTES;
  return Math.min(MAX_MINUTES, Math.max(MIN_MINUTES, x));
}

export function getSessionAlertMinutesBeforeExpiry() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw == null || raw === '') return SESSION_ALERT_DEFAULT_MINUTES;
    return clampMinutes(parseInt(raw, 10));
  } catch {
    return SESSION_ALERT_DEFAULT_MINUTES;
  }
}

export function setSessionAlertMinutesBeforeExpiry(minutes) {
  const m = clampMinutes(minutes);
  try {
    localStorage.setItem(STORAGE_KEY, String(m));
  } catch {
    /* ignore quota / private mode */
  }
  window.dispatchEvent(
    new CustomEvent(SESSION_ALERT_CHANGED_EVENT, { detail: { minutes: m } })
  );
  return m;
}

/** Preset minute values shown in the Session Alert settings dropdown (plus Custom). Longer windows use Custom (up to 24h). */
export function getSessionAlertOptions() {
  return [1, 2, 3, 5, 10, 15, 30, 45, 60];
}

/**
 * Combine whole hours and whole minutes (e.g. 1 hr + 20 min → 80 min), then clamp to valid range.
 * @param {number} hours - 0–24
 * @param {number} minutes - 0–59 (values with hours+minutes are clamped via total cap)
 */
export function totalMinutesFromHoursAndMinutes(hours, minutes) {
  const h = Math.max(0, Math.min(24, Math.round(Number(hours)) || 0));
  const mi = Math.max(0, Math.min(59, Math.round(Number(minutes)) || 0));
  const raw = h * 60 + mi;
  if (raw < 1) return SESSION_ALERT_DEFAULT_MINUTES;
  return clampMinutes(raw);
}
